import React, { useMemo, useState } from "react";
import { Alert, Card, Divider, Row, Typography, Space } from "antd";
import { CloseOutlined, EyeOutlined } from "@ant-design/icons";
import type { Booking, Product } from "../../types/booking";
import InvoiceCostOverviewDetails, {
  type CostOverviewDetailKind,
} from "./InvoiceCostOverviewDetails";
import { getBookingProducts } from "./invoiceFormHelpers";

const { Text, Link } = Typography;

export interface CostOverviewLine {
  description: string;
  quantity: number;
  unit_price: number;
  vat_percentage: number;
  cost_type?: string;
  manual?: boolean;
}

interface InvoiceCostOverviewProps {
  lines: CostOverviewLine[];
  loading?: boolean;
  booking?: Booking | null;
  onRemoveLine?: (index: number) => void;
  /**
   * Wizard step index: 0 Tariff, 1 Customs, 2 Delivery, 3 Review.
   * Hides costs that are not confirmed yet (customs until Customs step;
   * outbound / admin / LMD until LMD lines are actually added).
   */
  wizardStep?: number;
  /** True once the user has added LMD lines on the Delivery step. */
  hasLmdLines?: boolean;
  /** After HS codes change on booking products (Inklaringskosten detail). */
  onProductsHsUpdated?: (products: Product[]) => void | Promise<void>;
}

const REMOVABLE_TYPES = [
  "tariff", "transport", "customs_btw", "customs_duties", "customs_commission",
  "customs_commission_btw", "customs_commission_duties", "lmd_delivery",
  "lmd_diesel_surcharge", "lmd_nl_pallet_surcharge", "europallet_non_exchange",
];

const CUSTOMS_COST_TYPES = new Set([
  "customs_btw",
  "customs_duties",
  "customs_commission",
  "customs_commission_btw",
  "customs_commission_duties",
]);

const DELIVERY_COST_TYPES = new Set([
  "lmd_delivery",
  "lmd_diesel_surcharge",
  "lmd_nl_pallet_surcharge",
  "europallet_non_exchange",
  "lmd",
  "lmd_surcharge",
]);

/** Outbound/admin fees are synced from the Delivery (LMD) step — not final before then. */
function isDeliveryDependentLine(line: CostOverviewLine): boolean {
  if (line.cost_type && DELIVERY_COST_TYPES.has(line.cost_type)) return true;
  const lower = (line.description || "").toLowerCase();
  return (
    lower.includes("outbound") ||
    /administration fee outgoing/i.test(lower) ||
    lower.includes("last mile") ||
    lower.includes("lmd") ||
    lower.includes("diesel surcharge")
  );
}

function isCustomsLine(line: CostOverviewLine): boolean {
  if (line.cost_type && CUSTOMS_COST_TYPES.has(line.cost_type)) return true;
  const lower = (line.description || "").toLowerCase();
  return (
    lower.includes("btw bij invoer") ||
    lower.includes("invoerrechten") ||
    lower.includes("voorschotprovisie")
  );
}

/**
 * Only show costs confirmed for the current wizard step.
 * - Tariff: freight + fixed fees
 * - Customs: + customs
 * - Delivery: outbound/admin/LMD only after LMD is added
 * - Review: everything
 */
function isLineVisibleForWizardStep(
  line: CostOverviewLine,
  wizardStep: number | undefined,
  hasLmdLines: boolean
): boolean {
  if (wizardStep == null || wizardStep >= 3) return true;

  if (isDeliveryDependentLine(line)) {
    return hasLmdLines;
  }
  if (wizardStep < 1 && isCustomsLine(line)) return false;
  return true;
}

function getDetailKind(line: CostOverviewLine): CostOverviewDetailKind | null {
  const lower = (line.description || "").toLowerCase();
  const costType = (line.cost_type || "").toLowerCase();
  if (
    costType === "tariff" ||
    costType === "transport" ||
    lower.includes("freight") ||
    lower.includes("tariff")
  ) {
    return "products";
  }
  if (lower.includes("inklaring") || lower.includes("customs clearance")) {
    return "inklaring";
  }
  if (lower.includes("outbound")) return "shipments";
  return null;
}

function formatEuro(value: number): string {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
}

/**
 * Flat chronological cost list — same order as calculator kostenoverzicht
 * (lines are sorted when written to the form).
 */
const InvoiceCostOverview: React.FC<InvoiceCostOverviewProps> = ({
  lines,
  loading,
  booking = null,
  onRemoveLine,
  wizardStep,
  hasLmdLines = false,
  onProductsHsUpdated,
}) => {
  const [detailKind, setDetailKind] = useState<CostOverviewDetailKind | null>(null);

  const visibleLines = lines
    .map((line, idx) => ({ line, originalIndex: idx }))
    .filter(({ line }) =>
      isLineVisibleForWizardStep(line, wizardStep, hasLmdLines)
    );

  const subtotal = visibleLines.reduce(
    (s, { line: l }) => s + l.quantity * l.unit_price,
    0
  );
  const totalVat = visibleLines.reduce(
    (s, { line: l }) =>
      s + l.quantity * l.unit_price * (l.vat_percentage / 100),
    0
  );
  const grandTotal = subtotal + totalVat;

  const hasLines = visibleLines.length > 0;

  const missingHsCount = useMemo(() => {
    if (!booking) return 0;
    return getBookingProducts(booking).filter((p) => {
      const digits = String(p.hs_code ?? "").replace(/\D/g, "");
      return digits.length === 0;
    }).length;
  }, [booking]);

  return (
    <>
      <Card
        size="small"
        title="Cost Overview"
        style={{ position: "sticky", top: 16 }}
        bodyStyle={{ padding: "8px 12px" }}
      >
        {missingHsCount > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10, padding: "8px 10px" }}
            message={
              <Text strong style={{ fontSize: 13 }}>
                {missingHsCount} product{missingHsCount === 1 ? "" : "s"} missing an HS code
              </Text>
            }
            description={
              <Text style={{ fontSize: 12 }}>
                Inklaringskosten may be incomplete.{" "}
                <Link
                  onClick={(e) => {
                    e.preventDefault();
                    setDetailKind("inklaring");
                  }}
                  style={{ fontSize: 12 }}
                >
                  Add HS codes
                </Link>
              </Text>
            }
          />
        )}
        {!hasLines && !loading && (
          <Text
            type="secondary"
            style={{ textAlign: "center", display: "block", padding: "12px 0", fontSize: 11 }}
          >
            Select a booking and add lines to see the cost breakdown
          </Text>
        )}
        {loading && (
          <Text
            type="secondary"
            style={{ textAlign: "center", display: "block", padding: "12px 0", fontSize: 11 }}
          >
            Calculating...
          </Text>
        )}
        {hasLines && (
          <Space direction="vertical" style={{ width: "100%" }} size={2}>
            {visibleLines.map(({ line, originalIndex }) => {
              const detail = booking ? getDetailKind(line) : null;
              return (
                <Row
                  key={originalIndex}
                  justify="space-between"
                  align="middle"
                  style={{ marginTop: 2 }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      maxWidth: onRemoveLine ? "55%" : "65%",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: detail ? "pointer" : undefined,
                    }}
                    onClick={() => detail && setDetailKind(detail)}
                    title={detail ? "Click to view details" : undefined}
                  >
                    <Text
                      style={{
                        fontSize: 11,
                        color: detail ? "#1677ff" : undefined,
                      }}
                      ellipsis
                    >
                      {detail && (
                        <EyeOutlined style={{ marginRight: 4, fontSize: 10 }} />
                      )}
                      {line.description}
                    </Text>
                    {line.quantity > 1 && (
                      <Text type="secondary" style={{ fontSize: 10, display: "block" }}>
                        {Number(line.quantity.toFixed(3))} × {formatEuro(line.unit_price)}
                      </Text>
                    )}
                  </div>
                  <Space size={4} style={{ flexShrink: 0 }}>
                    <Text style={{ fontSize: 11 }}>
                      {formatEuro(line.quantity * line.unit_price)}
                    </Text>
                    {onRemoveLine && REMOVABLE_TYPES.includes(line.cost_type || "") && (
                      <CloseOutlined
                        style={{ fontSize: 9, color: "#999", cursor: "pointer", padding: 2 }}
                        onClick={() => onRemoveLine(originalIndex)}
                        onMouseEnter={(e) => {
                          (e.target as HTMLElement).style.color = "#ff4d4f";
                        }}
                        onMouseLeave={(e) => {
                          (e.target as HTMLElement).style.color = "#999";
                        }}
                      />
                    )}
                  </Space>
                </Row>
              );
            })}

            <Divider style={{ margin: "6px 0" }} />

            <Row justify="space-between">
              <Text style={{ fontSize: 12 }}>Subtotal</Text>
              <Text style={{ fontSize: 12 }}>{formatEuro(subtotal)}</Text>
            </Row>
            {totalVat > 0.005 && (
              <Row justify="space-between">
                <Text style={{ fontSize: 12 }}>VAT</Text>
                <Text style={{ fontSize: 12 }}>{formatEuro(totalVat)}</Text>
              </Row>
            )}
            <Divider style={{ margin: "4px 0" }} />
            <Row justify="space-between">
              <Text strong style={{ fontSize: 14 }}>Total</Text>
              <Text strong style={{ fontSize: 14 }}>
                {formatEuro(totalVat > 0.005 ? grandTotal : subtotal)}
              </Text>
            </Row>
          </Space>
        )}
      </Card>

      <InvoiceCostOverviewDetails
        kind={detailKind}
        booking={booking}
        onClose={() => setDetailKind(null)}
        onProductsHsUpdated={onProductsHsUpdated}
      />
    </>
  );
};

export default InvoiceCostOverview;
